import { useMemo } from 'react';
import { Tag, Typography } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { useCategoryRender } from './useCategoryRender';
import { useStatusRender } from './useStatusRender';
import { CATEGORY_FILTER_OPTIONS, STATUS_FILTER_OPTIONS } from '../constants';
import { useUserTimestampRender } from '@src/hooks';

const { Text } = Typography;

export const useOfferingsColumns = (): SmartDataTableProps['columns'] => {
  const getCategoryConfig = useCategoryRender();
  const getStatusConfig = useStatusRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'name',
        title: 'Name',
        dataIndex: 'name',
        sorter: false,
        minWidth: 200,
        render: (name: string) => (
          <Text style={{ fontWeight: 500, color: '#262626' }}>{name}</Text>
        ),
      },
      {
        key: 'category',
        title: 'Category',
        dataIndex: 'category',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...CATEGORY_FILTER_OPTIONS],
        render: (category: string) => {
          const config = getCategoryConfig(category);
          return (
            <Tag color={config.color} style={{ fontWeight: 500 }}>
              {config.icon} {config.text}
            </Tag>
          );
        },
      },
      {
        key: 'price',
        title: 'Price',
        dataIndex: 'price',
        sorter: true,
        minWidth: 120,
        align: 'right',
        render: (price: number) => (
          <Text style={{ fontWeight: 500 }}>₹{price}</Text>
        ),
      },
      {
        key: 'discount',
        title: 'Discount',
        dataIndex: 'discount',
        sorter: true,
        minWidth: 120,
        align: 'right',
        render: (discount: number) =>
          discount > 0 ? (
            <Text style={{ fontWeight: 500 }}>{discount}%</Text>
          ) : (
            <Text style={{ fontWeight: 500 }}>-</Text>
          ),
      },
      // {
      //   key: 'gst_rate',
      //   title: 'GST Rate',
      //   dataIndex: 'gst_rate',
      //   sorter: true,
      //   minWidth: 100,
      //   align: 'right',
      //   render: (rate: number) => <Text>{rate ? `${rate}%` : '-'}</Text>,
      // },
      // {
      //   key: 'is_gst_inclusive',
      //   title: 'GST Inclusive',
      //   dataIndex: 'is_gst_inclusive',
      //   minWidth: 100,
      //   render: (inclusive: boolean) => (
      //     <Tag color={inclusive ? 'green' : 'orange'}>
      //       {inclusive ? 'Inclusive' : 'Exclusive'}
      //     </Tag>
      //   ),
      // },
      {
        key: 'duration_in_mins',
        title: 'Duration',
        dataIndex: 'duration_in_mins',
        sorter: true,
        minWidth: 100,
        align: 'right',
        render: (mins: number) => (
          <Text style={{ textAlign: 'right' }}>
            {mins ? `${mins} mins` : '-'}
          </Text>
        ),
      },
      {
        key: 'total_sessions',
        title: 'Sessions',
        dataIndex: 'total_sessions',
        sorter: true,
        minWidth: 100,
        align: 'right',
        render: (sessions: number) => (
          <Text style={{ textAlign: 'right' }}>
            {sessions ? sessions : '1'}
          </Text>
        ),
      },
      {
        key: 'is_active',
        title: 'Status',
        dataIndex: 'is_active',
        minWidth: 100,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...STATUS_FILTER_OPTIONS],
        render: (status: number | boolean) => {
          // Convert numeric status to boolean
          const isActive = status === 1 || status === true;
          const config = getStatusConfig(isActive);
          return (
            <Tag
              icon={config.icon}
              style={{
                fontWeight: 500,
                backgroundColor: config.bgColor,
                color: config.textColor,
                borderColor: config.borderColor,
                border: `1px solid ${config.borderColor}`,
              }}
            >
              {config.text}
            </Tag>
          );
        },
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ],
    [getCategoryConfig, getStatusConfig, userTimestampRender]
  );
};
